// src/pages/superadmin/SuperAdminCompany.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Building, Mail, Phone, MapPin, Globe, Clock, Save,
  CheckCircle, Edit2, X, RefreshCw, FileText, Calendar,
  DollarSign, CreditCard
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const SuperAdminCompany = () => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    legal_name: '',
    ein: '',
    formation_date: '',
    incorporation_state: 'Texas',
    registered_agent_address: '',
    business_email: '',
    business_phone: '',
    website: '',
    business_address: '',
    business_city: '',
    business_state: '',
    business_zip: '',
    tax_method: 'single_member',
    fiscal_year: 'calendar',
    currency: 'USD'
  });

  useEffect(() => {
    loadCompany();
  }, []);

  const loadCompany = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/admin/platform/company`);
      if (response.data) {
        setCompany(response.data);
        setFormData({
          legal_name: response.data.legal_name || '',
          ein: response.data.ein || '',
          formation_date: response.data.formation_date || '',
          incorporation_state: response.data.incorporation_state || 'Texas',
          registered_agent_address: response.data.registered_agent_address || '',
          business_email: response.data.business_email || '',
          business_phone: response.data.business_phone || '',
          website: response.data.website || '',
          business_address: response.data.business_address || '',
          business_city: response.data.business_city || '',
          business_state: response.data.business_state || '',
          business_zip: response.data.business_zip || '',
          tax_method: response.data.tax_method || 'single_member',
          fiscal_year: response.data.fiscal_year || 'calendar',
          currency: response.data.currency || 'USD'
        });
      }
    } catch (error) {
      console.error('Error loading company:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/admin/platform/company`, formData);
      setSaveSuccess(true);
      setEditMode(false);
      await loadCompany();
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving company:', error);
      alert('Error saving company information');
    } finally {
      setSaving(false);
    }
  };

  const InfoRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3 p-3 bg-gray-900/50 rounded-xl mb-2">
      <Icon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-white break-words mt-0.5">{value || 'Not specified'}</p>
      </div>
    </div>
  );

  const SectionTitle = ({ icon: Icon, title }) => (
    <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
      <Icon className="w-4 h-4 text-blue-400" />
      {title}
    </h3>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-white">Company Information</h2>
          <p className="text-gray-400 text-sm">Manage your company profile and tax settings</p>
        </div>
        <div className="flex gap-2">
          {!editMode ? (
            <button
              onClick={() => setEditMode(true)}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white flex items-center gap-2"
            >
              <Edit2 className="w-4 h-4" />
              Edit
            </button>
          ) : (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-white flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => {
                  setEditMode(false);
                  loadCompany();
                }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {saveSuccess && (
        <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-400" />
          <span className="text-green-400 text-sm">Company information saved successfully!</span>
        </div>
      )}

      {/* View Mode */}
      {!editMode && company && (
        <>
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
            <SectionTitle icon={Building} title="Legal Information" />
            <InfoRow icon={Building} label="Legal Name" value={company.legal_name} />
            <InfoRow icon={FileText} label="EIN / SIRET" value={company.ein} />
            <InfoRow icon={Calendar} label="Formation Date" value={company.formation_date} />
            <InfoRow icon={MapPin} label="Incorporation State" value={company.incorporation_state} />
            <InfoRow icon={MapPin} label="Registered Agent Address" value={company.registered_agent_address} />
          </div>

          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
            <SectionTitle icon={Globe} title="Contact Information" />
            <InfoRow icon={Mail} label="Business Email" value={company.business_email} />
            <InfoRow icon={Phone} label="Business Phone" value={company.business_phone} />
            <InfoRow icon={Globe} label="Website" value={company.website} />
            <InfoRow icon={MapPin} label="Business Address" value={company.business_address} />
            <InfoRow icon={MapPin} label="City / State / Zip" value={`${company.business_city || ''} ${company.business_state || ''} ${company.business_zip || ''}`.trim()} />
          </div>

          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
            <SectionTitle icon={DollarSign} title="Tax & Financial Settings" />
            <InfoRow icon={FileText} label="Tax Method" value={company.tax_method === 'single_member' ? 'Single Member LLC' : 'Multi Member LLC'} />
            <InfoRow icon={Calendar} label="Fiscal Year" value={company.fiscal_year === 'calendar' ? 'Calendar Year' : 'Fiscal Year'} />
            <InfoRow icon={CreditCard} label="Currency" value={company.currency} />
          </div>
        </>
      )}

      {/* Edit Mode */}
      {editMode && (
        <div className="space-y-4">
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
            <SectionTitle icon={Building} title="Legal Information" />
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Legal Name</label>
                <input type="text" value={formData.legal_name} onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">EIN / SIRET</label>
                <input type="text" value={formData.ein} onChange={(e) => setFormData({ ...formData, ein: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Formation Date</label>
                <input type="date" value={formData.formation_date} onChange={(e) => setFormData({ ...formData, formation_date: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Incorporation State</label>
                <select value={formData.incorporation_state} onChange={(e) => setFormData({ ...formData, incorporation_state: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white">
                  <option value="Texas">Texas</option>
                  <option value="Delaware">Delaware</option>
                  <option value="Nevada">Nevada</option>
                  <option value="Florida">Florida</option>
                  <option value="Wyoming">Wyoming</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Registered Agent Address</label>
                <textarea value={formData.registered_agent_address} onChange={(e) => setFormData({ ...formData, registered_agent_address: e.target.value })}
                  rows="2" className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
            <SectionTitle icon={Globe} title="Contact Information" />
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Business Email</label>
                <input type="email" value={formData.business_email} onChange={(e) => setFormData({ ...formData, business_email: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Business Phone</label>
                <input type="tel" value={formData.business_phone} onChange={(e) => setFormData({ ...formData, business_phone: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Website</label>
                <input type="url" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" placeholder="https://..." />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Business Address</label>
                <input type="text" value={formData.business_address} onChange={(e) => setFormData({ ...formData, business_address: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">City</label>
                  <input type="text" value={formData.business_city} onChange={(e) => setFormData({ ...formData, business_city: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">State</label>
                  <input type="text" value={formData.business_state} onChange={(e) => setFormData({ ...formData, business_state: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Zip Code</label>
                  <input type="text" value={formData.business_zip} onChange={(e) => setFormData({ ...formData, business_zip: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
            <SectionTitle icon={DollarSign} title="Tax & Financial Settings" />
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Tax Method</label>
                <select value={formData.tax_method} onChange={(e) => setFormData({ ...formData, tax_method: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white">
                  <option value="single_member">Single Member LLC (Disregarded Entity)</option>
                  <option value="multi_member">Multi Member LLC (Partnership)</option>
                  <option value="c_corp">C Corporation</option>
                  <option value="s_corp">S Corporation</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Fiscal Year</label>
                <select value={formData.fiscal_year} onChange={(e) => setFormData({ ...formData, fiscal_year: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white">
                  <option value="calendar">Calendar Year (Jan 1 - Dec 31)</option>
                  <option value="fiscal">Fiscal Year</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Currency</label>
                <select value={formData.currency} onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white">
                  <option value="USD">USD - US Dollar ($)</option>
                  <option value="EUR">EUR - Euro (€)</option>
                  <option value="GBP">GBP - British Pound (£)</option>
                  <option value="CAD">CAD - Canadian Dollar (C$)</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stripe Integration (optional) */}
      {company?.stripe_account_id && (
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
          <SectionTitle icon={CreditCard} title="Stripe Integration" />
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span className="text-gray-300 text-sm">Connected to Stripe account: {company.stripe_account_id}</span>
          </div>
        </div>
      )}

      <div className="text-center">
        <p className="text-xs text-gray-500">
          This information appears on invoices, legal documents, and client communications.
        </p>
      </div>
    </div>
  );
};

export default SuperAdminCompany;